import { Vector, Index } from 'mdatools/arrays';
import { methodsList, prep_apply, prep_trim } from '@mdatools-apps/wasm/prep.js';
import { cloneFullData } from '../utils.js';

// the rows a preview shows: the creator and prep's apply tab take the same count, and the create
// tab's help text prints it
export const NROWS = 30;
// the longest chain, a mirror of MAX_NMETHODS in shared/wasm/c/prep_wasm.h (the C entry points
// refuse a longer one on their own)
export const MAX_NMETHODS = 8;


/**
 * Applies a preprocessing model to a full dataset and returns the preprocessed set: a clone of
 * 'd' around the new matrix, cut to the columns the model keeps. 'd' is left as it is. A caller
 * holding the set as state snapshots every field but 'values' first (see 'cloneFullData').
 * @param {Object} Module - WASM module.
 * @param {Object} d - JSON with full data.
 * @param {Object} model - preprocessing model.
 * @returns {Object} JSON with the preprocessed data.
 */
export function applyPrep(Module, d, model) {
   const values = prep_apply(Module, d.values, d.nresp, model, false, false);
   // 'prep_trim' cuts its argument in place, so it gets the clone and not 'd'
   return prep_trim(cloneFullData(d, values), model);
}


/**
 * Returns truncated (short) dataset for quick demonstration of preprocessing effect.
 * @param {Object} d - JSON with full data.
 * @param {number} n - number of rows to subset.
 */
export function getShortData(d, n) {
   // the kept rows are picked first and only they are copied: cut from a transpose of the whole
   // matrix, the preview used to cost two copies of the data for the rows it keeps
   const nrows = d.values.nrows;
   const offset = d.nresp > 0 ? d.nresp : 0;
   const step = (nrows < n ? 1 : Math.round(nrows / n));
   const rowInd = Index.seq(1, nrows, step);

   // predictors of the kept rows, transposed so objects are columns
   const predInd = Index.seq(offset + 1, d.values.ncols);
   const valuesShort = d.values.subset(rowInd, predInd).t();

   // the first response of the kept rows, if any
   const respShort = offset > 0 ? new Vector(d.values.subset(rowInd, 1).v) : null;
   const rowIndSet = new Set(rowInd.v);
   // 'rowInd' is 1-based, the array position is not
   const classesShort = d.classes ? d.classes.filter((v, i) => rowIndSet.has(i + 1)) : null;

   return {
      classNames: d.classNames,
      classes: classesShort,
      resp: respShort,
      values: valuesShort,
      varvalues: d.varvalues.filter((v, i) => i >= offset),
      varindices: d.varindices.filter((v, i) => i >= offset),
      varaxisLabel: d.varvaluesUnits ? d.varvaluesName + ' ' + d.varvaluesUnits : d.varvaluesName,
      varaxisUnits: d.varvaluesUnits
   }
}


/**
 * Creates a copy (clone) of short data.
 * @param {Object} d - JSON with short data
 * @param {Matrix} values - matrix with new (e.g. preprocessed) values to replace (if undefined will be taken from d.values).
 */
export function cloneShortData(d, values) {
   return {
      values: values ? values : d.values.copy(),
      varvalues: d.varvalues,
      varindices: d.varindices,
      varaxisLabel: d.varaxisLabel,
   }
}


/**
 * Checks that a value is a usable preprocessing model and throws when it is not. The messages
 * speak of a file: 'ModelLoader' shows them for a picked file, and prep's 'checkSession' only asks
 * whether a restored model passes.
 * @param {*} m - value read from a model file or from a stored session.
 */
export function checkPrepModel(m) {

   // every step here has to hold before the next one is allowed to look inside: reaching
   // 'm.class[0]' on a file whose 'class' is null raised a raw TypeError, and the user was shown
   // its text instead of a sentence about the file
   const isObject = v => v !== null && typeof v === 'object';
   if (!isObject(m) || !Array.isArray(m.class) || m.class[0] !== 'prepmodel') {
      throw Error('The JSON file you selected does not contain preprocessing model.');
   }

   // the lists are checked as lists before their lengths are read or summed: 'ml' and the
   // parameter arrays are typed after the JSON round trip when the file was written by the app,
   // and plain arrays when it was written by hand; a string there used to reach '.reduce' and
   // fail with a raw TypeError
   const isList = v => Array.isArray(v) || ArrayBuffer.isView(v);
   for (const key of ['ml', 'mpl', 'mp', 'mpl_new', 'mp_new']) {
      if (!isList(m[key])) {
         throw Error("Preprocessing model field '" + key + "' is missing or broken.");
      }
   }

   // validate method count consistency
   const nm = m.ml.length;
   if (nm < 1) {
      throw Error('Preprocessing model has no methods.');
   }
   if (m.mpl.length !== nm || m.mpl_new.length !== nm) {
      throw Error('Preprocessing model has inconsistent method counts.');
   }

   // the info panel renders the chain as soon as the model is set, in the template where no
   // catch reaches: it looks every method number up in 'methodsList' and reads a description
   // from 'info' for each. a number outside the list threw there, and so did a missing 'info';
   // one shorter than the chain rendered the word 'undefined'. the app cuts the creator's
   // eight-slot list to the chain, so 'info' is exactly as long; a model from an earlier build
   // carries every slot the creator kept, and a shorter list was never the creator's
   if (!Array.from(m.ml).every(v => Number.isInteger(v) && v >= 0 && v < methodsList.length)) {
      throw Error('Preprocessing model has a method number outside the list of methods.');
   }
   if (!Array.isArray(m.info) || m.info.length < nm) {
      throw Error('Preprocessing model has no description for each of its methods.');
   }

   // validate parameter array lengths match declared sizes
   const mpLen = m.mpl.reduce((a, v) => a + v, 0);
   const mpNewLen = m.mpl_new.reduce((a, v) => a + v, 0);
   if (m.mp.length !== mpLen) {
      throw Error('Preprocessing model has wrong parameter length (mp).');
   }
   if (m.mp_new.length !== mpNewLen) {
      throw Error('Preprocessing model has wrong parameter length (mp_new).');
   }

   // validate npred: a count, so a positive integer; NaN, a fraction and Infinity are none
   if (!Number.isInteger(m.npred) || m.npred < 1) {
      throw Error('Preprocessing model has invalid number of predictors.');
   }
}


/**
 * Checks the preprocessing a model file carries under 'prep' or a session under 'prepmodel'. The
 * apps write an empty object (a skipped chain), an empty list, 0 or null for none, and the readers
 * take those as none; anything else has to be a model 'checkPrepModel' accepts.
 * @param {*} p - value read from a model file or from a stored session.
 */
export function checkPrepField(p) {
   if (!p || (typeof p === 'object' && Object.keys(p).length === 0)) return;
   checkPrepModel(p);
}


/**
 * Checks that a value is a chain of preprocessing methods as the creator writes it and throws
 * when it is not: a list of entries, each an object with a known 'id' and a 'params' list of
 * that method's length holding numbers. The creator reads every entry at its init, in its
 * template and in its widgets, where nothing catches.
 * @param {*} chain - value read from a stored session.
 */
export function checkPrepChain(chain) {

   if (!Array.isArray(chain)) {
      throw Error('Preprocessing chain is not a list.');
   }

   const isList = v => Array.isArray(v) || ArrayBuffer.isView(v);
   for (const m of chain) {
      const method = m !== null && typeof m === 'object' && methodsList.find(v => v.id === m.id);
      if (!method) {
         throw Error('Preprocessing chain has a method outside the list of methods.');
      }
      if (!isList(m.params) || m.params.length !== method.mpl) {
         throw Error(`Preprocessing chain has wrong number of parameters for method '${m.id}'.`);
      }
      if (!Array.from(m.params).every(Number.isFinite)) {
         throw Error(`Preprocessing chain has a parameter of method '${m.id}' that is no number.`);
      }
   }
}
